import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import tenantService from './tenantService';
import TenantBrandingLogoField from './TenantBrandingLogoField';
import useCheckPermission from '../../hooks/useCheckPermission';
import DataList from '../../components/DataList';
import ColorPickerField from '../../components/ColorPickerField';
import { brandConfig } from '../../branding/brandConfig';
import { useLocationPicker } from '../../hooks/useLocationPicker';

const SESSION_TIMEOUT_PRESETS = [15, 30, 60, 120, 240, 480];
const RSS_SYNC_PRESETS = [15, 30, 60, 120, 360, 1440];

const hermesBrandingDefaults = {
  productName: brandConfig.productName,
  loginTitle: brandConfig.loginWelcome,
  primaryColor: brandConfig.colors.primary,
  secondaryColor: brandConfig.colors.secondary,
  logoUrl: null as string | null,
  logoPreviewUrl: null as string | null,
};

const withHermesDefaults = (raw: any = {}) => {
  const productName =
    raw?.productName || raw?.ProductName || hermesBrandingDefaults.productName;
  const loginTitle =
    raw?.loginTitle || raw?.LoginTitle || hermesBrandingDefaults.loginTitle;
  const primaryColor =
    raw?.primaryColor ||
    raw?.PrimaryColor ||
    hermesBrandingDefaults.primaryColor;
  const secondaryColor =
    raw?.secondaryColor ||
    raw?.SecondaryColor ||
    hermesBrandingDefaults.secondaryColor;
  const logoUrl = raw?.logoUrl ?? raw?.LogoUrl ?? null;
  const logoPreviewUrl =
    raw?.logoPreviewUrl ?? raw?.LogoPreviewUrl ?? null;

  return {
    ...hermesBrandingDefaults,
    ...raw,
    productName,
    loginTitle,
    primaryColor,
    secondaryColor,
    logoUrl,
    logoPreviewUrl,
  };
};

/**
 * SaaS platform admin screen for a tenant's commercial / ops settings.
 * Not tenant self-service — edits require ManageTenants.
 */
const TenantAdminPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const { canPlatform } = useCheckPermission();
  const activeTenantId = useSelector(
    (state: any) => state.user.userInfo?.activeTenantId
  );
  const tenantId = params.id || activeTenantId;
  const [settings, setSettings] = useState<any>(null);
  const [branding, setBranding] = useState<any>(withHermesDefaults());
  const [credentials, setCredentials] = useState<any[]>([]);
  const [revealedKey, setRevealedKey] = useState<string | null>(
    (location.state as any)?.apiKey ?? null
  );
  const canEdit = canPlatform(['ManageTenants']);
  const { LocationPickerButton, LocationPickerDialog } = useLocationPicker();

  const mapDefaultLat =
    typeof settings?.mapCenterLatitude === 'number' &&
    !Number.isNaN(settings.mapCenterLatitude)
      ? settings.mapCenterLatitude
      : 47.352746328290266;
  const mapDefaultLng =
    typeof settings?.mapCenterLongitude === 'number' &&
    !Number.isNaN(settings.mapCenterLongitude)
      ? settings.mapCenterLongitude
      : 19.09674477339744;

  const load = () => {
    if (!tenantId) return;
    dispatch({ type: 'SHOW_QUERY' });
    Promise.all([
      tenantService.getSettings(tenantId),
      tenantService.getBranding(tenantId),
      tenantService.listApiCredentials(tenantId),
    ])
      .then(([settingsRes, brandingRes, credsRes]) => {
        if (!settingsRes.hasError) setSettings(settingsRes.result);
        setBranding(
          brandingRes.hasError
            ? withHermesDefaults()
            : withHermesDefaults(brandingRes.result)
        );
        if (!credsRes.hasError) setCredentials(credsRes.records);
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  useEffect(() => {
    load();
  }, [tenantId]);

  if (!tenantId) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Nincs aktív bérlő kiválasztva.</Typography>
      </Paper>
    );
  }

  const saveSettings = () => {
    dispatch({ type: 'SHOW_SAVE' });
    tenantService
      .updateSettings(tenantId, settings)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Beállítások mentve', { variant: 'success' });
          setSettings(response.result);
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const saveBranding = () => {
    dispatch({ type: 'SHOW_SAVE' });
    tenantService
      .updateBranding(tenantId, {
        productName: branding.productName,
        loginTitle: branding.loginTitle,
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        logoUrl: branding.logoUrl,
        jsonExtensions: branding.jsonExtensions,
      })
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Branding mentve', { variant: 'success' });
          const next = withHermesDefaults(response.result);
          setBranding(next);
          if (
            String(activeTenantId ?? '').toLowerCase() ===
            String(tenantId ?? '').toLowerCase()
          ) {
            dispatch({ type: 'LOAD_TENANT_BRANDING', payload: next });
          }
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const rotateKey = () => {
    dispatch({ type: 'SHOW_SAVE' });
    tenantService
      .rotateApiCredential(tenantId, { name: 'Rotated mobile API key' })
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
        } else {
          const key = response.result?.apiKey ?? response.result?.ApiKey;
          setRevealedKey(key);
          enqueueSnackbar('Új API kulcs — másold le most', {
            variant: 'warning',
            persist: true,
          });
          load();
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Typography variant="h5">Bérlő platform beállítások</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Bérlő ID: {tenantId}
          </Typography>
        </Grid>

        {!canEdit && (
          <Grid size={12}>
            <Alert severity="warning">
              Csak megtekintés — a módosításhoz SaaS admin jogosultság kell.
            </Alert>
          </Grid>
        )}

        {revealedKey && (
          <Grid size={12}>
            <Alert severity="warning">
              API kulcs (másold le most): <strong>{revealedKey}</strong>
            </Alert>
          </Grid>
        )}

        <Grid size={12}>
          <Typography variant="h6">Működési beállítások</Typography>
          <Typography variant="body2" color="text.secondary">
            Üzemeltetési paraméterek ehhez a bérlőhöz (session, térkép, RSS).
          </Typography>
        </Grid>
        {settings && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Session timeout (perc)"
                helperText="CMS bejelentkezés lejárata"
                value={
                  SESSION_TIMEOUT_PRESETS.includes(
                    Number(settings.sessionTimeoutMinutes)
                  )
                    ? Number(settings.sessionTimeoutMinutes)
                    : settings.sessionTimeoutMinutes ?? 60
                }
                disabled={!canEdit}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    sessionTimeoutMinutes: Number(e.target.value),
                  })
                }
              >
                {[
                  ...SESSION_TIMEOUT_PRESETS,
                  ...(settings.sessionTimeoutMinutes &&
                  !SESSION_TIMEOUT_PRESETS.includes(
                    Number(settings.sessionTimeoutMinutes)
                  )
                    ? [Number(settings.sessionTimeoutMinutes)]
                    : []),
                ].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m} perc
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="RSS szinkron gyakoriság (perc)"
                helperText="Külső RSS import háttérjob intervallum"
                value={
                  RSS_SYNC_PRESETS.includes(
                    Number(settings.rssSyncFrequencyMinutes)
                  )
                    ? Number(settings.rssSyncFrequencyMinutes)
                    : settings.rssSyncFrequencyMinutes ?? 60
                }
                disabled={!canEdit}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    rssSyncFrequencyMinutes: Number(e.target.value),
                  })
                }
              >
                {[
                  ...RSS_SYNC_PRESETS,
                  ...(settings.rssSyncFrequencyMinutes &&
                  !RSS_SYNC_PRESETS.includes(
                    Number(settings.rssSyncFrequencyMinutes)
                  )
                    ? [Number(settings.rssSyncFrequencyMinutes)]
                    : []),
                ].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m} perc
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Térkép középpont — szélesség"
                helperText="CMS térképek és aggregátor helyjavaslat"
                value={settings.mapCenterLatitude ?? ''}
                disabled
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Térkép középpont — hosszúság"
                helperText="CMS térképek és aggregátor helyjavaslat"
                value={settings.mapCenterLongitude ?? ''}
                disabled
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            {canEdit && (
              <Grid size={12}>
                <LocationPickerButton />
                <LocationPickerDialog
                  defaultLat={mapDefaultLat}
                  defaultLong={mapDefaultLng}
                  onLocationSelected={(location: {
                    lat: number;
                    lng: number;
                    address: string;
                  }) => {
                    setSettings({
                      ...settings,
                      mapCenterLatitude: location.lat,
                      mapCenterLongitude: location.lng,
                    });
                  }}
                />
              </Grid>
            )}

            <Grid size={12}>
              <Typography variant="h6">Modulok (üzemeltetési kapcsoló)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Licence / entitlement mellett ez a SaaS oldali kill-switch.
                Kikapcsolva a modul API-k és a feature flag is tiltja a funkciót,
                még ha van is entitlement.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.parkingOperationallyEnabled}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        parkingOperationallyEnabled: e.target.checked,
                      })
                    }
                  />
                }
                label="Parkolás üzemszerűen engedélyezve"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.htmlAppletsOperationallyEnabled}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        htmlAppletsOperationallyEnabled: e.target.checked,
                      })
                    }
                  />
                }
                label="HTML appletek üzemszerűen engedélyezve"
              />
            </Grid>
            {canEdit && (
              <Grid size={12}>
                <Button variant="contained" onClick={saveSettings}>
                  Beállítások mentése
                </Button>
              </Grid>
            )}
          </>
        )}

        <Grid size={12}>
          <Typography variant="h6">White-label branding</Typography>
          <Typography variant="body2" color="text.secondary">
            Belépő / termék megjelenés ehhez a bérlőhöz. Üres mezőknél a
            Hermész alapértelmezések érvényesülnek. A futó mobilapp kinézetét
            külön az „Applikáció témák” kezeli.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Terméknév"
            placeholder={hermesBrandingDefaults.productName}
            value={branding.productName ?? ''}
            disabled={!canEdit}
            onChange={(e) =>
              setBranding({ ...branding, productName: e.target.value })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Belépő oldal szöveg"
            placeholder={hermesBrandingDefaults.loginTitle}
            value={branding.loginTitle ?? ''}
            disabled={!canEdit}
            onChange={(e) =>
              setBranding({ ...branding, loginTitle: e.target.value })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPickerField
            label="Elsődleges szín"
            value={branding.primaryColor ?? ''}
            defaultColor={hermesBrandingDefaults.primaryColor}
            readonly={!canEdit}
            onChange={(hex) =>
              setBranding({ ...branding, primaryColor: hex })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ColorPickerField
            label="Másodlagos szín"
            value={branding.secondaryColor ?? ''}
            defaultColor={hermesBrandingDefaults.secondaryColor}
            readonly={!canEdit}
            onChange={(hex) =>
              setBranding({ ...branding, secondaryColor: hex })
            }
          />
        </Grid>
        <Grid size={12}>
          <TenantBrandingLogoField
            tenantId={String(tenantId)}
            logoUrl={branding.logoUrl}
            logoPreviewUrl={branding.logoPreviewUrl}
            readonly={!canEdit}
            onUploaded={(next) => {
              const resolved = withHermesDefaults(next);
              setBranding(resolved);
              if (
                String(activeTenantId ?? '').toLowerCase() ===
                String(tenantId ?? '').toLowerCase()
              ) {
                dispatch({ type: 'LOAD_TENANT_BRANDING', payload: resolved });
              }
            }}
            onCleared={() =>
              setBranding({
                ...branding,
                logoUrl: null,
                logoPreviewUrl: null,
              })
            }
          />
        </Grid>
        {canEdit && (
          <Grid size={12}>
            <Button variant="contained" onClick={saveBranding}>
              Branding mentése
            </Button>
          </Grid>
        )}

        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography variant="h6">Mobil API kulcsok</Typography>
              <Typography variant="body2" color="text.secondary">
                Flutter / public API (<code>X-API-KEY</code>). Forgatás után a
                régi kulcs azonnal érvénytelen.
              </Typography>
            </Box>
            {canEdit && (
              <Button variant="outlined" color="warning" onClick={rotateKey}>
                API kulcs forgatása
              </Button>
            )}
          </Box>
        </Grid>
        <Grid size={12}>
          <DataList
            rows={credentials}
            columns={[
              { field: 'name', headerName: 'Név', flex: 100 },
              { field: 'environment', headerName: 'Környezet', flex: 80 },
              { field: 'keyPrefix', headerName: 'Prefix', flex: 60 },
              { field: 'createdAt', headerName: 'Létrehozva', flex: 100 },
              { field: 'revokedAt', headerName: 'Visszavonva', flex: 100 },
            ]}
          />
        </Grid>

        <Grid size={12}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Vissza
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TenantAdminPage;
